/**
 * useStandaloneLinks — iOS PWA "stay in standalone" fix.
 *
 * Problem: when a Web App Manifest PWA is opened from the iOS home screen
 * (display:standalone), any <a href="..."> click pops the user OUT of the
 * standalone shell and into Safari with tab chrome — the classic iOS PWA bug
 * that has shipped since 2018.
 *
 * Fix: at the document level, intercept clicks on same-origin <a> elements
 * (without target="_blank") and navigate via the History API so the
 * Single-Page App stays inside the standalone window. External links and
 * anything explicitly targeting _blank are left alone (they correctly open
 * in Safari, which is expected behaviour).
 *
 * Safe to mount once at the top of the app — the listener is idempotent and
 * cleaned up on unmount.
 */
import { useEffect } from "react";

function isStandalone() {
  // iOS Safari exposes navigator.standalone; other browsers expose the CSS
  // display-mode media query. Either one means we are running as a PWA.
  return (
    (typeof navigator !== "undefined" && navigator.standalone === true) ||
    (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches)
  );
}

export default function useStandaloneLinks() {
  useEffect(() => {
    if (!isStandalone()) return;

    const handler = (e) => {
      // Ignore modifier-clicks (user explicitly wants a new tab)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0) return; // left click only

      const link = e.target.closest("a[href]");
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href) return;
      // Allow hash-only, mailto:, tel:, sms: to behave natively
      if (href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("sms:")) return;

      const target = link.getAttribute("target");
      if (target === "_blank") return; // explicitly open in Safari

      let url;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      // External origin → let Safari handle it (expected UX)
      if (url.origin !== window.location.origin) return;

      // Internal SPA navigation while staying in standalone shell
      e.preventDefault();
      const path = url.pathname + url.search + url.hash;
      if (path === window.location.pathname + window.location.search + window.location.hash) return;
      window.history.pushState({}, "", path);
      // React Router listens for popstate; dispatch so the route updates.
      window.dispatchEvent(new PopStateEvent("popstate"));
    };

    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);
}
